import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { post } from "../../lib/request"
import { toPayloadEventId } from "../../lib/utils/eventRecordId"

function sanitizeCheckMyCopyPayload(payload = {}) {
  const body = { ...payload }
  const normalizedEventId = toPayloadEventId(body.eventId)
  if (normalizedEventId) {
    body.eventId = normalizedEventId
  } else {
    delete body.eventId
  }
  return body
}

export const checkMyCopy = createAsyncThunk("checkMyCopy/check", async ({ clientId, payload }, { rejectWithValue }) => {
  try {
    const response = await post(
      `posts/clients/${clientId}/check-my-copy`,
      sanitizeCheckMyCopyPayload(payload),
    )
    return response
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message)
  }
})

export const recalibrateScore = createAsyncThunk(
  "checkMyCopy/recalibrate",
  async ({ clientId, payload }, { rejectWithValue }) => {
    try {
      const response = await post(
        `/posts/clients/${clientId}/check-my-copy`,
        sanitizeCheckMyCopyPayload({
          ...payload,
          mode: "recalibrate_score_only",
        }),
      )
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  },
)

const checkMyCopySlice = createSlice({
  name: "checkMyCopy",
  initialState: {
    status: "idle",
    error: null,
    checkResult: null,
    brandToneScore: null,
    issues: [],
    correctedText: "",
    acceptedIssues: [],
  },
  reducers: {
    resetCheckMyCopy: (state) => {
      state.status = "idle"
      state.error = null
      state.checkResult = null
      state.brandToneScore = null
      state.issues = []
      state.correctedText = ""
      state.acceptedIssues = []
    },
    acceptIssue: (state, action) => {
      const issueId = action.payload
      if (!state.acceptedIssues.includes(issueId)) {
        state.acceptedIssues.push(issueId)
      }
    },
    acceptAllIssues: (state) => {
      state.acceptedIssues = state.issues.map((issue) => issue.suggestion_id)
    },
  },
  extraReducers: (builder) => {
    builder
      // Check my copy
      .addCase(checkMyCopy.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(checkMyCopy.fulfilled, (state, action) => {
        state.status = "succeeded"
        const postData = action.payload?.post?.[0]
        if (postData) {
          state.checkResult = postData
          state.brandToneScore = postData.brand_tone_score
          state.issues = postData.issues || []
          state.correctedText = postData.post_copy || ""
        }
      })
      .addCase(checkMyCopy.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      // Recalibrate score only — keep issues list and check status unchanged (no full re-check UX)
      .addCase(recalibrateScore.fulfilled, (state, action) => {
        const postData = action.payload?.post?.[0]
        if (postData?.brand_tone_score != null) {
          state.brandToneScore = postData.brand_tone_score
        }
      })
  },
})

export const { resetCheckMyCopy, acceptIssue, acceptAllIssues } = checkMyCopySlice.actions
export default checkMyCopySlice.reducer
